import { ForwardedRef, SubmitEvent, useEffect, useImperativeHandle, useState } from 'react';
import { convertSecondsToMinutes } from '@/helpers/formatHelpers';

const CODE_LENGTH = 6;
const REQUEST_TIMER_DELAY = 59;
const INITIAL_CODE = Array(CODE_LENGTH).fill('');

export const useVerificationCode = (
  ref: ForwardedRef<unknown>,
  onResend: () => void,
  onConfirm: (code: string) => void
) => {
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [isDisabledSendCode, setIsDisabledSendCode] = useState(false);
  const [codeChanged, setCodeChanged] = useState(false);
  const [code, setCode] = useState(INITIAL_CODE);

  const formattedCode = code.join('');
  const isDisabledSubmit = formattedCode.length !== CODE_LENGTH;

  useImperativeHandle(ref, () => {
    return {
      resetTimer(retryAfter: string | null) {
        const timerValue = retryAfter ? Number(retryAfter) : REQUEST_TIMER_DELAY;
        const { seconds: secondsValue, minutes: minutesValues } = convertSecondsToMinutes(timerValue);

        setMinutes(minutesValues);
        setSeconds(secondsValue);
        setIsDisabledSendCode(true);
      },

      resetCodeChanged() {
        setCodeChanged(false);
      },
    };
  }, []);

  useEffect(() => {
    if (seconds === 0 && minutes === 0) {
      setIsDisabledSendCode(false);
    }
  }, [minutes, seconds]);

  const startTimer = () => {
    if (minutes === 0 && seconds === 0) {
      onResend();
    }

    setIsDisabledSendCode(true);
  };

  const changeCode = (newCode: string[]) => {
    setCodeChanged(true);
    setCode(newCode);
  };

  const onSubmitCode = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (formattedCode.length !== CODE_LENGTH) {
      return;
    }

    onConfirm(formattedCode);
  };

  return {
    codeChanged,
    minutes,
    seconds,
    code,
    isDisabledSendCode,
    startTimer,
    setSeconds,
    setMinutes,
    changeCode,
    isDisabledSubmit,
    onSubmitCode,
  };
};
